import React from 'react';
import { GrabFlow, AutoGradeEngine, GradeBakeParity } from './GrabGradeDiagrams';

// In-app deep-dive on how a single video frame becomes a finished, region-graded
// photo. Mirrors the real code: backend/api/stills.js (the worker +
// computeAutoFrameGrade), backend/api/perceive.js (the Lab-cluster perception),
// backend/api/grade.js + frontend/src/components/GradedCanvas.js (the bake/preview
// parity), and backend/_autobake.mjs (the terminal refine path).
export default function FrameGradeDoc() {
  return (
    <>
      <p className="cs-section-sub">
        How a still gets pulled from RED footage and developed into a finished photo — preserving the
        full negative, measuring each wave with a small perception model, and grading it per frame so
        every wave keeps its own colour. These diagrams trace the real code path.
      </p>

      {/* 1. Non-destructive still workflow */}
      <div className="cs-doc-card">
        <h2>1 · The still workflow, end to end</h2>
        <div className="cs-doc-what">
          A frame grab is non-destructive: we keep the full-resolution flat negative as a 16-bit
          master and store the grade as editable parameters, so any still can be re-developed or
          re-cropped later from the original detail — never from a baked JPEG.
        </div>
        <div className="cs-pipe-diagram"><GrabFlow /></div>
        <ol className="cs-doc-steps">
          <li><b>Grab.</b> The Frame Grabber scrubs a clip, you pick a frame, and it writes a <code>stillGrab</code> request to Firestore — a lightweight queue the worker watches.</li>
          <li><b>Decode at full res.</b> The stills worker pulls the source and decodes the exact frame at its native resolution — RED <code>.R3D</code> through the RED SDK, regular video via ffmpeg input-seek.</li>
          <li><b>16-bit master.</b> The frame is preserved as a <b>16-bit PNG</b> flat log negative (not an 8-bit JPEG). This is the fix for the earlier quality drop — 8-bit masters banded and macroblocked in the sky and water; 16-bit holds the gradients.</li>
          <li><b>Per-frame auto-grade.</b> The negative is measured and a grade tuned to <i>this</i> frame is computed (the engine in §2), then stored on the asset.</li>
          <li><b>Bake look + crop.</b> ffmpeg applies the grade and crop to the master at 16-bit, then emits derivatives: a small <code>thumb</code>, a ~3000px <code>magazine</code> view, and a <b>full-resolution JPEG</b>.</li>
          <li><b>Store &amp; serve.</b> Derivatives go to S3 / CDN and the grade is recorded on the asset doc. The Photos grid and Lightbox load the magazine view first, then progressively upgrade to the full-res image once the view settles.</li>
        </ol>
      </div>

      {/* 1b. The fixed order: reframe → upscale → grade */}
      <div className="cs-doc-card">
        <h2>1b · The order is fixed: reframe → upscale → grade</h2>
        <div className="cs-doc-what">
          Recompose and enlarge <i>before</i> grading, never after. The frame is developed in one
          fixed order so every stage sees the right pixels and nothing has to be redone.
        </div>
        <div className="cs-doc-how">
          <b>decode / de-log → reframe (crop / zoom) → AI upscale → GRADE → emit.</b> Reframe and
          upscale run on the <b>working negative</b> before any look, so (a) the AI upscaler works on
          clean, ungraded pixels — grading artefacts never get amplified, (b) the auto-grade’s metering
          and the local masks resolve on the content that’s actually in frame at delivery resolution,
          and (c) the full-frame <b>master negative</b> stays a re-croppable, re-gradeable source of
          truth forever. Colour grading is always the <b>last</b> stage.
          <br /><br />
          <b>Two-tier negatives.</b> The <b>master</b> is full-frame, immutable, 16-bit (RED = flat
          Log3G10, scene-referred). The <b>working negative</b> is the cropped-and-upscaled version the
          grade actually runs on, and it’s <b>display-referred</b>. That difference matters: a look
          tuned against the log master looks wrong (darker, contrastier) on the display-referred working
          negative — so a final grade is always tuned against the same base the bake uses. When a frame
          is upscaled we bake the look’s own tonal decode into the working negative up front, so the
          upscaled result matches the mid-res proof it was approved from instead of coming out dark.
          <br /><br />
          <b>Upscaling keeps it photographic.</b> Enlargement runs on a self-hosted GPU that tiles the
          frame with overlap + feathered blending (no seams, no resolution cap). The default model
          preserves water/foam micro-texture; a small texture/sharpen + grain is added back in the look
          so a big enlargement reads as film, not plastic. Upscaling can’t invent detail that wasn’t
          captured, so a distant, soft subject is helped by a wider crop or a sharper frame — not by
          more sharpening. See “AI upscaling &amp; max-res delivery” for the full method.
        </div>
      </div>

      {/* 1c. Draft-then-full / proof workflow */}
      <div className="cs-doc-card">
        <h2>1c · Dial the look cheaply: draft → proof → sample → full</h2>
        <div className="cs-doc-what">
          Iterating a grade shouldn’t cost a full-resolution (or upscaled) bake on every tweak. There’s
          a fast lane for decisions and a slow lane only for the final commit.
        </div>
        <div className="cs-doc-how">
          <ul className="cs-doc-bullets">
            <li><b>Live preview</b> — the WebGL editor is instant and covers most look decisions with no bake at all.</li>
            <li><b>Draft bake</b> — re-bakes the <i>real</i> pipeline (crop + grade + masks) at a small long edge with no upscale, sub-second even for masked grades, to confirm the true result. The card shows a <b>DRAFT</b> badge until a full-quality bake replaces it.</li>
            <li><b>Proof sheet</b> — for a whole clip, mid-res subject-tracked crops every N frames laid out in clip order (3 per row, frame-labelled) so you can see the range of light and framing across the wave.</li>
            <li><b>Look test</b> — a handful of frames baked against two or three candidate grades side by side, so you <i>pick</i> a look instead of guessing one and re-baking everything.</li>
            <li><b>Sample upscales → full set</b> — only once a look is chosen, a few frames are upscaled to full res to confirm texture, and only then is the whole set enlarged. The expensive full-res upscale is always last.</li>
          </ul>
        </div>
      </div>

      {/* 2. Perception-driven auto-grade */}
      <div className="cs-doc-card">
        <h2>2 · Per-frame auto-grade (perception-driven)</h2>
        <div className="cs-doc-what">
          Each wave has its own light and colour, so the grade is computed per frame from what’s
          actually in it — not a single fixed preset stamped onto everything.
        </div>
        <div className="cs-pipe-diagram"><AutoGradeEngine /></div>
        <div className="cs-doc-how">
          <b>Auto-exposure.</b> Because the negative is flat log, exposure is set from the frame’s
          gray median mapped through a log-aware curve — a portable JS median (a 256-bin histogram),
          not an ImageMagick <code>fx:median</code> expression, which the production build doesn’t support.
          <br /><br />
          <b>Perception (the “transformer-free” vision step).</b> Generic semantic-segmentation models
          (we tried SegFormer-B0/ADE20K) systematically misread surf scenes — calling a barrel “rock”
          and whitewater “sand” — because the training domain is streets and rooms, not the ocean. So
          perception is a purpose-built classical measurer in <code>perceive.js</code>: it converts the
          developed frame to <b>CIE-Lab</b>, runs <b>k-means</b> on the colour, and labels each cluster
          into surf regions (sky, water, whitewater, barrel, sand, rock) from its Lab statistics and
          vertical position. It’s fast, deterministic, CPU-only, and right for this domain.
          <br /><br />
          <b>Region white balance — the key fix.</b> A surf frame is dominated by teal water, so a naïve
          whole-frame “gray-world” balance reads the ocean as a colour cast and warms the whole image to
          cancel it — wrecking the water. Instead, WB is measured from genuinely-neutral regions
          (<b>whitewater foam</b> first, then <b>sky</b>), so the water’s real colour is preserved while
          the neutrals come out neutral.
          <br /><br />
          <b>Water colour-key mask + adaptive tonal.</b> From the measured water hue/chroma we auto-place
          a colour-keyed mask (§3) that deepens the water’s own teal selectively, and tonal moves
          (contrast / whites / blacks) come from the region histogram with <b>spray-highlight protection</b>
          so bright foam doesn’t clip. Everything is written into one editable per-frame grade.
        </div>
      </div>

      {/* 3. Colour-key region mask */}
      <div className="cs-doc-card">
        <h2>3 · Region colour-key mask (hue / chroma gate)</h2>
        <div className="cs-doc-what">
          Surf colours overlap: teal water and blue sky sit close in hue, so the HSL mixer and a plain
          luminance keyer can’t cleanly separate them. The colour-key mask qualifies a region by both
          its hue <i>and</i> its colourfulness.
        </div>
        <div className="cs-doc-how">
          A new <code>color</code> mask type joins the existing radial and linear masks. It covers the
          whole frame and then gates each pixel through a <b>chromaticity</b> formulation
          (<code>u = 2R − G − B</code>, <code>v = √3 (G − B)</code>): a feathered window on hue angle around
          the target, multiplied by a feathered <b>minimum-chroma</b> threshold. That double gate lets it
          grab the saturated cyan of the water while rejecting the desaturated, paler blue of the sky.
          The auto-grade places one of these over the perceived water; you can also add and tune them by
          hand, and the AI assistant can place them from plain language. A bitmap/segment mask for the
          surfer (not colour-separable) is the planned next addition.
        </div>
      </div>

      {/* 4. Bake parity */}
      <div className="cs-doc-card">
        <h2>4 · Preview == export (bake parity)</h2>
        <div className="cs-doc-what">
          What you see while editing is exactly what bakes out — including the new colour-key mask.
        </div>
        <div className="cs-pipe-diagram"><GradeBakeParity /></div>
        <div className="cs-doc-how">
          The grade’s per-pixel colour/tone (input transform, exposure, WB, curves, HSL, wheels)
          compiles to a 3D <code>.cube</code> LUT, and each mask — radial, linear, or colour-key —
          becomes an ffmpeg <code>geq</code> alpha. The <b>same math</b> runs in the WebGL2 preview shader
          (<code>GradedCanvas.js</code>), so the live preview and the ffmpeg 16-bit bake match to within
          about 1/255 — verified with a per-pixel parity test for the colour-key gate against a JS mirror
          of the shader. The bake order is <code>lut3d</code> → spatial filters → mask <code>geq</code>,
          producing the same derivatives as the still workflow.
        </div>
      </div>

      {/* 5. Refine: in-app + terminal */}
      <div className="cs-doc-card">
        <h2>5 · Refine: per-frame, in-app or by terminal</h2>
        <div className="cs-doc-what">
          The auto-grade is a starting point, not a verdict. Any frame (or set of frames) can be nudged
          after the fact — “slightly less teal”, “warmer barrel”, “re-bake with a tighter crop” — and
          re-baked from the preserved master.
        </div>
        <div className="cs-doc-how">
          <ul className="cs-doc-bullets">
            <li><b>In-app.</b> The Photo studio opens the same WebGL grade engine on the still, where you adjust sliders, place/tune colour-key and shape masks, copy/paste a whole look between photos, and re-bake — all non-destructive, off the 16-bit master.</li>
            <li><b>Agentic.</b> The AI colour assistant reads the actual frames + region measurements and proposes slider / mask edits as validated JSON you preview and accept.</li>
            <li><b>Terminal.</b> <code>_autobake.mjs</code> drives the same path from the command line — calibrate the auto-grade, bake a small <b>proof</b> set for approval, then batch re-bake the whole library at 16-bit with region-aware grading. The grade is just data, so terminal and in-app edits are interchangeable.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
